import type {ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {
  buildSkillOverflowMenuSections,
  type SkillOverflowMenuHandlers,
} from 'llm/chat-library/skills/SkillOverflowMenuContext';
import type {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';

/**
 * Maps a domain SkillSummary into the shared list row view-model. All per-skill behavior (toggle
 * gating, which menu items appear) lives here as plain data + closures — the shared row component
 * stays generic. This is the "ViewModel" seam: `SkillSummary` (Model) -> `ListItemViewModel`.
 *
 * The overflow menu mirrors {@link SkillActionsMenu} (the menu shown in the legacy skills list and
 * the detail view) so a skill surfaces the same actions everywhere: Try with chat, Edit with chat
 * (if editable), Download skill, Manage access (if the user can manage sharing), Archive (if
 * archivable), and Export audit log. All items live in a single section to match that menu's flat,
 * divider-less dropdown.
 */
export function toSkillListItem(
  skill: SkillSummary,
  handlers: SkillOverflowMenuHandlers & {
    onToggleEnabled: (skill: SkillSummary, enabled: boolean) => Promise<void>;
  }
): ListItemViewModel {
  const {onToggleEnabled, ...menuHandlers} = handlers;

  return {
    id: skill.id,
    title: skill.name,
    // Byline + clamped description, matching the existing SkillCard layout.
    subtitle: skill.creatorName != null ? `by ${skill.creatorName}` : undefined,
    description: skill.description ?? undefined,
    toggle: {
      checked: skill.isEnabled,
      ariaLabel: skill.isEnabled ? `Disable ${skill.name}` : `Enable ${skill.name}`,
      tooltip: skill.isEnabled
        ? 'Disable skill (agents will not use it in your chats)'
        : 'Enable skill (agents will use it in your chats)',
      onChange: (enabled) => onToggleEnabled(skill, enabled),
    },
    menu: buildSkillOverflowMenuSections(skill, menuHandlers),
  };
}
